import * as Yup from 'yup';
import { Namespace, TFunction } from 'react-i18next';
import { DbsSettings } from 'models/DbsSettings';

export const MAX_CUSTOM_NAVIGATION_ITEMS = 3;

// The dbsNavigation column widths.
export const NAVIGATION_NAME_MAX_LENGTH = 255;
export const NAVIGATION_URL_MAX_LENGTH = 1024;
export const BOOK_NOW_TITLE_MAX_LENGTH = 50;

// The site's Book Now button reads its label from the navigation item with this name.
export const BOOK_NOW_NAVIGATION_NAME = 'book now';
export const BOOK_NOW_NAVIGATION_URL = '/search';

export const isBookNowNavigationItem = ({ name }: { name?: string | null }) =>
  name?.trim().toLowerCase() === BOOK_NOW_NAVIGATION_NAME;

export interface NavigationFormRow {
  key: string;
  uid?: string;
  name: string;
  url: string;
  openLinkInNewTab: boolean;
}

export interface NavigationSettingsFormValues {
  bookNowTitle: string;
  navigation: NavigationFormRow[];
}

const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const HTTP_URL = /^https?:\/\/\S+$/i;

export const normalizeNavigationUrl = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed || trimmed.startsWith('/') || HAS_SCHEME.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

export const isSupportedNavigationUrl = (value?: string) => {
  const normalized = normalizeNavigationUrl(value ?? '');

  return !normalized || normalized.startsWith('/') || HTTP_URL.test(normalized);
};

export const createNavigationRow = (): NavigationFormRow => ({
  key: crypto.randomUUID(),
  name: '',
  url: '',
  openLinkInNewTab: false,
});

export const getNavigationSettingsDefaultValues = (
  dbsSettings?: DbsSettings,
): NavigationSettingsFormValues => {
  const navigation = dbsSettings?.navigation ?? [];

  return {
    bookNowTitle: navigation.find(isBookNowNavigationItem)?.title ?? '',
    navigation: navigation
      .filter((item) => !isBookNowNavigationItem(item))
      .map(({ uid, name, url, openLinkInNewTab }) => ({
        key: uid,
        uid,
        name,
        url,
        openLinkInNewTab,
      })),
  };
};

export const getNavigationSettingsSchema = (t: TFunction<Namespace<'en'>>) => ({
  bookNowTitle: Yup.string()
    .trim()
    .max(
      BOOK_NOW_TITLE_MAX_LENGTH,
      t('pageDirectBookingEngine.navigationSettings.titleTooLong', {
        max: BOOK_NOW_TITLE_MAX_LENGTH,
      }),
    ),
  navigation: Yup.array()
    .max(
      MAX_CUSTOM_NAVIGATION_ITEMS,
      t('pageDirectBookingEngine.navigationSettings.tooManyLinks', {
        max: MAX_CUSTOM_NAVIGATION_ITEMS,
      }),
    )
    .of(
      Yup.object({
        name: Yup.string()
          .trim()
          .required(
            t('pageDirectBookingEngine.navigationSettings.titleRequired'),
          )
          .max(
            NAVIGATION_NAME_MAX_LENGTH,
            t('pageDirectBookingEngine.navigationSettings.titleTooLong', {
              max: NAVIGATION_NAME_MAX_LENGTH,
            }),
          )
          .test(
            'reserved-navigation-title',
            t('pageDirectBookingEngine.navigationSettings.titleReserved'),
            (value) => !isBookNowNavigationItem({ name: value }),
          ),
        url: Yup.string()
          .trim()
          .required(
            t('pageDirectBookingEngine.navigationSettings.linkRequired'),
          )
          .test(
            'navigation-url-length',
            t('pageDirectBookingEngine.navigationSettings.linkTooLong', {
              max: NAVIGATION_URL_MAX_LENGTH,
            }),
            (value) =>
              normalizeNavigationUrl(value ?? '').length <=
              NAVIGATION_URL_MAX_LENGTH,
          )
          .test(
            'supported-navigation-url',
            t('pageDirectBookingEngine.navigationSettings.linkInvalid'),
            isSupportedNavigationUrl,
          ),
        openLinkInNewTab: Yup.boolean(),
      }),
    ),
});
